import {
  ArrowPathIcon,
  PlusCircleIcon,
  XMarkIcon,
  CheckIcon,
  PlusIcon,
} from "@heroicons/react/24/solid";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TextInput, ToggleInput, CheckboxInput, SelectInput } from "../components/Inputs";
import { PageHeader } from "../components/Layouts";
import { ActiveFilter, PrimaryTable } from "../components/Tables";
import { useDevManager, useLoaderStore, useUserStore } from "../context";
import { useAlert, useApi } from "../hooks";
import constants from "../utils/constants";
import dateHelpers from "../utils/dateHelpers";
import { convertToLabelValueList, formatCurrency, formatPhoneNumber, resolveDurationCount, resolveSystemTenantId, switchSortDirection, userHasRole } from "../utils/helpers";
import { PrimaryButton } from "../components/Buttons";
import { Popover } from "@headlessui/react";

const EMPTY_FILTERS = {
  activeOnly: false,
};

export default function Products() {
  const navigate = useNavigate();
  const alert = useAlert();
  const { post, fetch } = useApi();
  const { setShowLoader } = useLoaderStore();
  const { currentUser, activeSystemTenant, systemTenants } = useUserStore();
  const { IS_DEV } = useDevManager();
  const [activeFilters, setActiveFilters] = useState({
    ...EMPTY_FILTERS,
    activeOnly: true,
    productTypeId: undefined
  });
  const [stagedFilters, setStagedFilters] = useState(activeFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDirection, setSortDirection] = useState(
    constants.SORT_DIRECTIONS.ASCENDING
  );
  const [sortField, setSortField] = useState("id");
  const [displayCount, setDisplayCount] = useState(
    constants.DISPLAY_PER_PAGE ?? 25
  );
  const [data, setData] = useState({ list: [], totalCount: 0 });
  const [systemTenantList, setSystemTenantList] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const isSysAdmin = userHasRole(currentUser, constants.ROLE_IDS.SYS_ADMIN);

  useEffect(() => {
    getData();
    getSystemTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, displayCount, activeFilters, sortDirection, sortField, activeSystemTenant]);

  useEffect(() => {
    setStagedFilters(activeFilters);
    setCurrentPage(1);
  }, [activeFilters]);

  const getData = () => {
    setIsLoading(true);
    fetch(`admin/productList`, {
      // systemTenantId: activeSystemTenant?.id,
      pageNumber: currentPage,
      pageSize: displayCount,
      sortDirection,
      sortField,
      isExact: false,
      systemTenantId: resolveSystemTenantId(currentUser, activeSystemTenant),
      ...activeFilters,
    })
      .then((res) => {
        setData(res);
      })
      .catch((err) => {
        alert("Server error", "Could not retrieve products", "error");
        console.error(err);
      })
      .finally(() => setIsLoading(false));
  };
  const getSystemTenants = () => {
    if (Array.isArray(systemTenants) && systemTenants.length) {
      setSystemTenantList(systemTenants);
      return;
    }
    setIsLoading(true);
    fetch(`admin/GetSystemTenantsSimple`, {
      // pageNumber: currentPage,
      // pageSize: displayCount,
      sortDirection: 'asc',
      sortField: 'name',
      isExact: false,
      systemTenantId: resolveSystemTenantId(currentUser, activeSystemTenant),
    })
      .then((res) => {
        if (Array.isArray(res))
          setSystemTenantList(convertToLabelValueList(res));
      })
      .catch((err) => {
        alert("Server error", "Could not retrieve products", "error");
        console.error(err);
      })
      .finally(() => setIsLoading(false));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(switchSortDirection(sortDirection));
    } else {
      setSortField(field);
    }
  };

  const toggleActiveStatus = (product) => {
      setShowLoader(true);
      post(`admin/toggleProductActive/${product.id}`)
        .then((res) => {
          alert("Success",
            res.message || "Product active status toggled",
            "success");
          getData();
        })
        .catch((err) => {
          alert("Error",
            err.data.message || "Product active status could not be toggled",
            "error");
          console.error(err);
        })
        .finally(() => {
          setShowLoader(false);
        });
  };
  // Insert toggleActiveStatus here

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between">
        <PageHeader title="Manage Products" />
        <PlusCircleIcon
          className="w-8 text-primaryColor hover:text-secondaryColor cursor-pointer"
          onClick={() => navigate("/products/0")}
        />
      </div>

      {/* Filter section */}
      <ProductFilterSection
        stagedFilters={stagedFilters}
        setStagedFilters={setStagedFilters}
        activeFilters={activeFilters}
        setActiveFilters={setActiveFilters}
        systemTenantList={systemTenantList}
      />

      {/* Table */}
      <PrimaryTable
        isLoading={isLoading}
        totalCount={data?.totalCount}
        displayCount={displayCount}
        currentPage={currentPage}
        setDisplayCount={setDisplayCount}
        setCurrentPage={setCurrentPage}
        header={
          <PrimaryTable.Header>
            { IS_DEV &&
            <PrimaryTable.HeaderColumn
              display="Id"
              onSort={() => handleSort("id")}
              isSortingBy={sortField === "id"}
              sortDirection={sortDirection}
            />}
            <PrimaryTable.HeaderColumn
              display="Name"
              onSort={() => handleSort("name")}
              isSortingBy={sortField === "name"}
              sortDirection={sortDirection}
            />
            <PrimaryTable.HeaderColumn
              display="Price"
              onSort={() => handleSort("price")}
              isSortingBy={sortField === "price"}
              sortDirection={sortDirection}
            />
            <PrimaryTable.HeaderColumn
              display="Duration"
              onSort={() => handleSort("duration")}
              isSortingBy={sortField === "duration"}
              sortDirection={sortDirection}
            />
            <PrimaryTable.HeaderColumn
              display="Product Type"
              onSort={() => handleSort("productTypeId")}
              isSortingBy={sortField === "productTypeId"}
              sortDirection={sortDirection}
            />
            {/* Insert column headers here */}
            {isSysAdmin && (
            <PrimaryTable.HeaderColumn 
              display="System Tenant"
              onSort={() => handleSort("systemTenant")}
              isSortingBy={sortField === "systemTenant"}
              sortDirection={sortDirection}
            />)}
            <PrimaryTable.HeaderColumn display="" />
          </PrimaryTable.Header>
        }
      >
        {data &&
          data.list &&
          data.list.map((x, i) => (
            <PrimaryTable.Row
              key={`${x.id}-${i}`}
              index={i}
              onClick={(e) => {
                if (!x.deactivatedAt) {
                  navigate(`/products/${x.id}`);
                }
              }}
            >
              {IS_DEV && <PrimaryTable.Cell>{x.id}</PrimaryTable.Cell>}
              <PrimaryTable.Cell>
                {x.name}
              </PrimaryTable.Cell>
              <PrimaryTable.Cell>
                {formatCurrency(x.price, true)}
              </PrimaryTable.Cell>
              <PrimaryTable.Cell>
                {resolveDurationCount(x.durationType, x.durationCount)}
              </PrimaryTable.Cell>
              <PrimaryTable.Cell>
                {constants.PRODUCT_TYPES.find(p => p.value === x.productType)?.label }
              </PrimaryTable.Cell>
              {isSysAdmin && 
              (<PrimaryTable.Cell>
                {x.systemTenantName}
              </PrimaryTable.Cell>)}
              {/* Insert cells here */}
              <PrimaryTable.Cell>
                <div className="flex">
{x.deactivatedAt ? (
    <ArrowPathIcon
        title="Activate"
        className="w-6 mx-1 text-white bg-green-500 rounded-md p-0.5 cursor-pointer"
        onClick={(e) => {
            e.stopPropagation();
            toggleActiveStatus(x);
        }}
    />
) : (
    <XMarkIcon
        title="Deactivate"
        className="w-6 mx-1 text-white bg-delete rounded-md p-0.5 cursor-pointer"
        onClick={(e) => {
            e.stopPropagation();
            toggleActiveStatus(x);
        }}
    />
)}
                  {/* Insert flip active status here */}
                </div>
              </PrimaryTable.Cell>
            </PrimaryTable.Row>
          ))}
      </PrimaryTable>
    </div>
  );
}

const ProductFilterSection = ({
  stagedFilters,
  setStagedFilters,
  activeFilters,
  setActiveFilters,
  systemTenantList
}) => {
  return (
    <div className="relative mb-4 rounded-xs shadow-sm bg-white p-4">
      <Popover as="div" className="relative">
        {({ open }) => (
          <>
            <Popover.Button className="flex items-center text-sm bg-gray-100 px-3 py-2 rounded-sm hover:bg-gray-200 relative focus:outline-none">
              <PlusIcon className="w-4 mr-1" />
              FILTERS
            </Popover.Button>
            <Popover.Panel
              as="div"
              className="absolute grid gap-y-4 z-50 w-full sm:w-1/3 origin-bottom-right bg-white rounded-md p-4 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none mt-1"
            >
              <div>
                <CheckboxInput
                  name="active-only"
                  label="Show only active products"
                  checked={stagedFilters.activeOnly === true}
                  onChange={() =>
                    setStagedFilters({
                      ...stagedFilters,
                      activeOnly: stagedFilters.activeOnly ? false : true,
                    })
                  }
                />
              </div>
              <div>
                <TextInput
                  label="Name"
                  placeholder="Enter Name" to filter by
                  value={stagedFilters.name}
                  onChange={(e) =>
                    setStagedFilters({
                      ...stagedFilters,
                      name: e.target.value || undefined,
                    })
                  }
                />
              </div>
              <div>
                <SelectInput
                  label="Product Type"
                  options={constants.PRODUCT_TYPES}
                  isSearchable
                  value={constants.PRODUCT_TYPES.find(x => x.value === stagedFilters.productType)}
                  onChange={x => {
                    setStagedFilters({
                      ...stagedFilters,
                      productType: x.value || undefined,
                    });
                  }}
                  onClear={() => {
                    setStagedFilters({
                      ...stagedFilters,
                      productType: undefined,
                    });
                  }}
                />
              </div>
              <div className={Array.isArray(systemTenantList) ? "" : "hidden"}>
                <SelectInput
                  label="System Tenant"
                  options={systemTenantList}
                  isSearchable
                  value={systemTenantList?.find(x => x.value === stagedFilters.systemTenantId)}
                  onChange={x => {
                    setStagedFilters({
                      ...stagedFilters,
                      systemTenantId: x.value || undefined,
                    });
                  }}
                  onClear={() => {
                    setStagedFilters({
                      ...stagedFilters,
                      systemTenantId: undefined,
                    });
                  }}
                />
              </div>
              {/* Add filters here */}
              <PrimaryButton
                text="Apply filters"
                onClick={() => setActiveFilters(stagedFilters)}
              />
            </Popover.Panel>
          </>
        )}
      </Popover>

      {/* Selected filters */}
      {Object.values(activeFilters).find(
        (val) => val !== undefined && val !== false
      ) ? (
        <div className="flex items-center space-x-2 pt-6 text-sm">
          <div>Selected:</div>
          <ActiveFilter
            display="Show active only"
            value={
              activeFilters.activeOnly ? (
                <CheckIcon className="w-4 text-green-600" />
              ) : undefined
            }
            onClear={() =>
              setActiveFilters({
                ...activeFilters,
                activeOnly: false,
              })
            }
          />

          <ActiveFilter
            display="Name"
            value={activeFilters.name}
            onClear={() =>
              setActiveFilters({
                ...activeFilters,
                name: undefined,
              })
            }
          />
          <ActiveFilter
            display="Product Type"
            value={constants.PRODUCT_TYPES.find(x => !!x.value && x.value === activeFilters.productType)?.label}
            onClear={() =>
              setActiveFilters({
                ...activeFilters,
                productTypeId: undefined,
              })
            }
          />
          <ActiveFilter
            display="System Tenant"
            value={systemTenantList?.find(x => x.value === activeFilters.systemTenantId)?.label}
            onClear={() =>
              setActiveFilters({
                ...activeFilters,
                systemTenantId: undefined,
              })
            }
          />
          {/* Add active filters here */}

          <div
            className="cursor-pointer bg-gray-100 px-3 py-2 text-xs hover:bg-gray-200"
            onClick={() => {
              setActiveFilters({ ...EMPTY_FILTERS });
            }}
          >
            Clear all
          </div>
        </div>
      ) : (
        <div className="pt-6 text-sm text-gray-400">No active filters</div>
      )}
    </div>
  );
};
